import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { formatDateTime } from '../../services/expenseVouchersApi';
import { formatMoney, WALLET_TYPE_LABELS } from '../../services/expenseAccountsApi';

const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };

// One card per currency. Balances are never added together across currencies.
export const WalletBalances = ({ balances = [] }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }} data-testid="wallet-balances">
    {balances.length === 0 && <Typography color="text.secondary">No wallet balance yet.</Typography>}
    {balances.map((b) => (
      <Card key={b.currency} sx={{ minWidth: 180, borderLeft: 4, borderColor: b.balance < 0 ? 'error.main' : 'success.main' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">{b.currency} balance</Typography>
          <Typography variant="h6" color={b.balance < 0 ? 'error.main' : 'text.primary'} sx={{ fontWeight: 700 }}>
            {formatMoney(b.balance, b.currency)}
          </Typography>
          {b.balance < 0 && <Typography variant="caption" color="error">Negative balance</Typography>}
        </CardContent>
      </Card>
    ))}
  </Box>
);

// Ledger table (paginated server-side). voucherLink(entry) -> optional link target.
const WalletLedger = ({ data, page, rowsPerPage, onPage, onRowsPerPage, currency, onCurrency, voucherLink }) => {
  const entries = data?.entries || [];
  const currencies = (data?.balances || []).map((b) => b.currency);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Wallet history</Typography>
        {onCurrency && (
          <TextField select size="small" label="Currency" value={currency || ''} onChange={(e) => onCurrency(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All currencies</MenuItem>
            {currencies.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date / Time', 'Type', 'Voucher', 'Amount', 'Balance after', 'By', 'Comment'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary">No wallet transactions</Typography></TableCell></TableRow>
            )}
            {entries.map((e) => {
              const link = e.voucher && voucherLink ? voucherLink(e) : null;
              return (
                <TableRow key={e._id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.at)}</TableCell>
                  <TableCell>
                    {WALLET_TYPE_LABELS[e.type] || e.type}
                    {e.type === 'REIMBURSABLE' && typeof e.requestedAmount === 'number' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        on {formatMoney(e.requestedAmount, e.currency)} claimed
                      </Typography>
                    )}
                    {['ADVANCE', 'NEW_ADVANCE'].includes(e.type) && typeof e.requestedAmount === 'number' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatMoney(e.creditedAmount ?? e.amount, e.currency)} credited of {formatMoney(e.requestedAmount, e.currency)} requested
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{link ? <Link component={RouterLink} to={link}>{e.voucherNumber}</Link> : (e.voucherNumber || '-')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: e.amount < 0 ? 'error.main' : 'success.main' }}>
                    {e.amount > 0 ? '+' : ''}{formatMoney(e.amount, e.currency)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: e.balanceAfter < 0 ? 'error.main' : undefined }}>{formatMoney(e.balanceAfter, e.currency)}</TableCell>
                  <TableCell>{e.byName || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{e.comment || ''}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.pagination?.totalItems || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(ev, p) => onPage(p)}
          onRowsPerPageChange={(ev) => onRowsPerPage(parseInt(ev.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </>
  );
};

export default WalletLedger;
